import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '@/shared/config/firebase';

export interface UserInfo {
  customerId: string; // ID du customer (identifiant business unique)
  email: string;
  firstName?: string;
  lastName?: string;
  totalOrders: number;
  lastOrderDate?: Date;
}

/**
 * Repository pour récupérer les informations utilisateur depuis les commandes
 */
export class UserInfoRepository {
  /**
   * Récupère les informations d'un utilisateur depuis ses commandes par customerId
   */
  static async getUserInfoByCustomerId(customerId: string, organizationId: string): Promise<UserInfo | null> {
    try {
      const ordersCollection = collection(db, 'orders');
      const q = query(
        ordersCollection,
        where('customerId', '==', customerId),
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc'),
        limit(1)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const latestOrder = querySnapshot.docs[0].data();
      
      // Compter le nombre total de commandes
      const countQuery = query(
        ordersCollection,
        where('customerId', '==', customerId),
        where('organizationId', '==', organizationId)
      );
      const countSnapshot = await getDocs(countQuery);
      
      return {
        customerId: customerId,
        email: latestOrder.orderInfo?.email || '',
        firstName: latestOrder.orderInfo?.firstName,
        lastName: latestOrder.orderInfo?.lastName,
        totalOrders: countSnapshot.size,
        lastOrderDate: latestOrder.createdAt?.toDate()
      };
    } catch (error) {
      console.error('❌ Erreur getUserInfoByCustomerId:', error);
      return null;
    }
  }

  /**
   * Récupère les informations d'un utilisateur depuis ses commandes par email (rétrocompatibilité)
   */
  static async getUserInfoByEmail(email: string, organizationId: string): Promise<UserInfo | null> {
    try {
      const ordersCollection = collection(db, 'orders');
      const q = query(
        ordersCollection,
        where('orderInfo.email', '==', email),
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc'),
        limit(1)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const latestOrder = querySnapshot.docs[0].data();
      
      // Compter le nombre total de commandes
      const countQuery = query(
        ordersCollection,
        where('orderInfo.email', '==', email),
        where('organizationId', '==', organizationId)
      );
      const countSnapshot = await getDocs(countQuery);
      
      return {
        customerId: latestOrder.customerId || email, // Utiliser customerId si disponible
        email: latestOrder.orderInfo?.email || email,
        firstName: latestOrder.orderInfo?.firstName,
        lastName: latestOrder.orderInfo?.lastName,
        totalOrders: countSnapshot.size,
        lastOrderDate: latestOrder.createdAt?.toDate()
      };
    } catch (error) {
      console.error('❌ Erreur getUserInfoByEmail:', error);
      return null;
    }
  }

  /**
   * Récupère les informations de plusieurs utilisateurs par customerId
   */
  static async getUsersInfoByCustomerIds(customerIds: string[], organizationId: string): Promise<Map<string, UserInfo>> {
    const usersMap = new Map<string, UserInfo>();
    
    if (customerIds.length === 0) {
      return usersMap;
    }
    
    try {
      // Firestore limite les requêtes 'in' à 10 éléments
      const chunks = [];
      for (let i = 0; i < customerIds.length; i += 10) {
        chunks.push(customerIds.slice(i, i + 10));
      }
      
      for (const chunk of chunks) {
        const ordersCollection = collection(db, 'orders');
        // Retirer orderBy pour éviter l'index composite - trier côté client
        const q = query(
          ordersCollection,
          where('customerId', 'in', chunk),
          where('organizationId', '==', organizationId)
        );
        
        const querySnapshot = await getDocs(q);
        
        // Grouper par customerId et prendre la commande la plus récente
        const customerOrdersMap = new Map<string, any[]>();
        
        querySnapshot.docs.forEach(doc => {
          const data = doc.data();
          const customerId = data.customerId;
          
          if (customerId) {
            if (!customerOrdersMap.has(customerId)) {
              customerOrdersMap.set(customerId, []);
            }
            customerOrdersMap.get(customerId)!.push(data);
          }
        });
        
        // Construire les UserInfo avec tri côté client
        customerOrdersMap.forEach((orders, customerId) => {
          // Trier par createdAt desc côté client
          orders.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
            return dateB - dateA; // Tri décroissant
          });
          
          const latestOrder = orders[0]; // Commande la plus récente après tri
          
          usersMap.set(customerId, {
            customerId: customerId,
            email: latestOrder.orderInfo?.email || '',
            firstName: latestOrder.orderInfo?.firstName,
            lastName: latestOrder.orderInfo?.lastName,
            totalOrders: orders.length,
            lastOrderDate: latestOrder.createdAt?.toDate()
          });
        });
      }
      
      return usersMap;
    } catch (error) {
      console.error('❌ Erreur getUsersInfoByCustomerIds:', error);
      return usersMap;
    }
  }

  /**
   * Récupère les informations de plusieurs utilisateurs en une seule fois par email (rétrocompatibilité)
   */
  static async getUsersInfoByEmails(emails: string[], organizationId: string): Promise<Map<string, UserInfo>> {
    const usersMap = new Map<string, UserInfo>();
    
    if (emails.length === 0) {
      return usersMap;
    }
    
    try {
      // Firestore limite les requêtes 'in' à 10 éléments
      const chunks = [];
      for (let i = 0; i < emails.length; i += 10) {
        chunks.push(emails.slice(i, i + 10));
      }
      
      for (const chunk of chunks) {
        const ordersCollection = collection(db, 'orders');
        // Retirer orderBy pour éviter l'index composite - trier côté client
        const q = query(
          ordersCollection,
          where('orderInfo.email', 'in', chunk),
          where('organizationId', '==', organizationId)
        );
        
        const querySnapshot = await getDocs(q);
        
        // Grouper par email et prendre la commande la plus récente
        const emailOrdersMap = new Map<string, any[]>();
        
        querySnapshot.docs.forEach(doc => {
          const data = doc.data();
          const email = data.orderInfo?.email;
          
          if (email) {
            if (!emailOrdersMap.has(email)) {
              emailOrdersMap.set(email, []);
            }
            emailOrdersMap.get(email)!.push(data);
          }
        });
        
        // Construire les UserInfo avec tri côté client
        emailOrdersMap.forEach((orders, email) => {
          // Trier par createdAt desc côté client
          orders.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
            return dateB - dateA; // Tri décroissant
          });
          
          const latestOrder = orders[0]; // Commande la plus récente après tri
          
          usersMap.set(email, {
            customerId: latestOrder.customerId || email, // Utiliser customerId si disponible
            email: latestOrder.orderInfo?.email || email,
            firstName: latestOrder.orderInfo?.firstName,
            lastName: latestOrder.orderInfo?.lastName,
            totalOrders: orders.length,
            lastOrderDate: latestOrder.createdAt?.toDate()
          });
        });
      }
      
      return usersMap;
    } catch (error) {
      console.error('❌ Erreur getUsersInfoByEmails:', error);
      return usersMap;
    }
  }
}
